/** @file fillPartImage: a dropped or pasted PNG into the tile a part shows now (fit prompt on a misfit). */
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import { fillSlotImage } from '../art/fit/fillSlotImage';
import { fitChoice } from '../art/fitChoice';
import { readPngSize } from '../art/readPngSize';
import { docEntryOf } from './docEntryOf';
import { shownTileOf } from './shownTileOf';
import type { ViewportCtx } from './viewportCtx';

/**
 * `png` into the shown tile of drawable `drawableIndex` (`shownTileOf`): an image of the slot's size fills as is,
 * another size asks Pad / Scale / Cancel (the server adapts it, `slot.fill.fit`); then upload and one `slot.fill`
 * labelled "Fill <slot> from <how>". A toast says why when nothing can be filled. True when it landed.
 */
export async function fillPartImage(ctx: ViewportCtx, drawableIndex: number, png: Uint8Array, how: 'drop' | 'paste'): Promise<boolean> {
  const s = ctx.store.getState();
  const ref = shownTileOf(ctx, drawableIndex);
  const entry = ref === null ? null : docEntryOf(ctx.store, ref);
  if (ref === null || entry === null) { s.notify('warning', `Nothing to ${how} onto: that part shows no tile here`); return false; }
  if (ctx.deps === null) { s.notify('error', `No server to upload the ${how === 'drop' ? 'dropped' : 'pasted'} image to`); return false; }
  const slot = slotKey(ref.drawable, ref.entry, ref.tile);
  let size: [number, number];
  try {
    size = readPngSize(png);
  } catch (e) {
    s.notify('error', `Not a PNG: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }
  const exact = fitChoice(size, entry.size) === 'exact';
  const fit = exact ? undefined : await ctx.askFit([{ name: `${how}.png`, slot, png, size, slotSize: [entry.size[0], entry.size[1]] }]);
  if (fit === null) return false;
  return fillSlotImage(ctx.store, ctx.deps, slot, png, fit, `Fill ${slot} from ${how}`);
}
